import { useQueryClient } from "@tanstack/react-query";
import { type CSSProperties, Fragment, type ReactNode, Suspense, useEffect, useState } from "react";
import { gtmMarketsQuery, sourcePrioritizationQuery, useGlobals } from "../../lib/dashboard-api";
import { lazyRetry } from "../../lib/lazy-retry";
import { sectionPreloader, workspaceDataKey, workspacePreloadKey } from "../../lib/section-preload";
import { COMBINED_RUN_ID, explorerQuery } from "../../lib/segmentation-api";
import { APP_NAME, APP_NAME_LINES, COUNTRY, COUNTRY_FULL, VISION_YEAR } from "../../lib/tenant";
import { useTheme } from "../../lib/theme";
import { useBelow } from "../../lib/use-breakpoint";
// Only the launcher pill ships here; the drawer itself is code-split inside
// assistant-panel.tsx, so the AI SDK stays out of the first paint.
import { AssistantPanel } from "./assistant-panel";
// Flag only — the section body itself stays lazy (see BehaviorTrendsSection).
import { SHOW_LEGACY_BEHAVIOR_WALL } from "./behavior-trends-shared";
// The default-visible "global" 1.1 section stays eager — lazy-loading it would
// only add a Suspense flash on first paint (its heavy charts self-defer inside).
import { ComingSoonSection } from "./coming-soon-section";
import { GlobalSection } from "./global-section";
import { LandingHub } from "./landing-hub";
import { MobileShell } from "./mobile-shell";
// Chrome stays eager — the tree, section header/shell, present-mode context,
// the workspace switcher/hub and design tokens render on every view, so
// code-splitting them would only add a Suspense flash without saving bytes
// that matter.
import { PresentContext } from "./present-context";
import { SectionErrorBoundary } from "./section-error-boundary";
import { AccentWord } from "./section-header";
import { SectionShell } from "./section-shell";
import { SectionSkeleton } from "./section-skeleton";
import { type SectionGroup, type SectionItem, SectionTree } from "./section-tree";
import { DASH_SHELL_CSS } from "./shell-css";
import { C, FM, FS, FU, T, TREE_W, TREE_W_COLLAPSED, TYPE, W, accentInk } from "./tokens";
import {
  WORKSPACES,
  WORKSPACE_ACCENT,
  WORKSPACE_BY_ID,
  WORKSPACE_LABEL,
  type WorkspaceId,
  workspaceAvailable,
} from "./workspaces";

// The Segmentation workspace IS the Demand-Sensing engine (Explorer + Traveler
// Archetypes), rendered as two sections inside the dashboard shell's left
// section-tree — not a standalone app with its own top-bar tabs. Lazy so its
// ~640KB stays out of the main bundle until a Segmentation section is opened.
const SegmentationExplorerSection = lazyRetry(() =>
  import("../segmentation-ui/sections").then((m) => ({ default: m.SegmentationExplorerSection })),
);
const SegmentationSegmentsSection = lazyRetry(() =>
  import("../segmentation-ui/sections").then((m) => ({ default: m.SegmentationSegmentsSection })),
);
// 1.3 — the run/workbook console. Its own chunk (the workbook editor is heavy
// and most viewers only read 1.1/1.2), and admin-gated inside the section.
const SegmentationConsoleSection = lazyRetry(() =>
  import("../segmentation-ui/console-section").then((m) => ({ default: m.SegmentationConsoleSection })),
);
// The workspace's front door — the dark landing + methodology dossier the user
// passes through on the way into the Explorer (see `segIntroSeen` below).
const EngineLanding = lazyRetry(() =>
  import("../segmentation-ui/engine-landing").then((m) => ({ default: m.EngineLanding })),
);
// Product Matching's front door — one screen, four tiles, one per section
// (see `productHubSeen` below). Same contract as the segmentation landing above.
const ProductHub = lazyRetry(() => import("./product-hub").then((m) => ({ default: m.ProductHub })));

// Every section is its own code-split chunk (origin/main's optimization). The
// dashboard renders one section at a time (page-per-section switch below), so a
// user on section 1.1 shouldn't pay to download the 1300-line prioritization
// model, the D3 maps or recharts (~110 kB via ThreeCharts) until they navigate
// there. Each chunk loads on first visit, then caches. The active section is
// wrapped in a Suspense boundary (both the normal shell and the present stage).
const AffordabilitySection = lazyRetry(() =>
  import("./affordability-section").then((m) => ({ default: m.AffordabilitySection })),
);
const BehaviorTrendsSection = lazyRetry(() =>
  import("./behavior-trends-section").then((m) => ({ default: m.BehaviorTrendsSection })),
);
const BookingSection = lazyRetry(() => import("./booking-section").then((m) => ({ default: m.BookingSection })));
const ConnectivitySection = lazyRetry(() =>
  import("./connectivity-section").then((m) => ({ default: m.ConnectivitySection })),
);
const CorridorsSection = lazyRetry(() => import("./corridors-section").then((m) => ({ default: m.CorridorsSection })));
const ForecastsSection = lazyRetry(() => import("./forecasts-section").then((m) => ({ default: m.ForecastsSection })));
const GtmMarketBriefSection = lazyRetry(() =>
  import("./gtm-market-brief-section").then((m) => ({ default: m.GtmMarketBriefSection })),
);
const GtmConnectivitySection = lazyRetry(() =>
  import("./gtm-connectivity-section").then((m) => ({ default: m.GtmConnectivitySection })),
);
const GtmDistributionSection = lazyRetry(() =>
  import("./gtm-distribution-section").then((m) => ({ default: m.GtmDistributionSection })),
);
const GtmVisaSection = lazyRetry(() => import("./gtm-visa-section").then((m) => ({ default: m.GtmVisaSection })));
const GtmProductSection = lazyRetry(() =>
  import("./gtm-product-section").then((m) => ({ default: m.GtmProductSection })),
);
const GtmComingSoon = lazyRetry(() => import("./gtm-coming-soon").then((m) => ({ default: m.GtmComingSoon })));
const InboundSection = lazyRetry(() => import("./inbound-section").then((m) => ({ default: m.InboundSection })));
const PurposeSection = lazyRetry(() => import("./purpose-section").then((m) => ({ default: m.PurposeSection })));
// Intelligence · Saudi scope — full section set ported from the demand-sensing
// mock (`saudi-intel-sections.tsx`), restyled to the TQ light theme. These
// supersede the earlier `saudi-demand-section` / `saudi-sentiment-section`
// placeholders.
const SaudiDemandSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiDemandSection })),
);
const SaudiSourceMarketSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiSourceMarketSection })),
);
const SaudiDestinationsSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiDestinationsSection })),
);
const SaudiTargetSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiTargetSection })),
);
const SaudiViralSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiViralSection })),
);
const SaudiSearchSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiSearchSection })),
);
const SaudiBookingSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiBookingSection })),
);
const SaudiConnectivitySection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiConnectivitySection })),
);
const SaudiAccommodationSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiAccommodationSection })),
);
const SaudiVisaSection = lazyRetry(() =>
  import("./saudi-intel-sections").then((m) => ({ default: m.SaudiVisaSection })),
);
const SaudiPotentialProcessSection = lazyRetry(() =>
  import("./saudi-potential-process-section").then((m) => ({ default: m.SaudiPotentialProcessSection })),
);
// Prioritization peer — one folded "Prioritization engine" section: an idle
// loader whose CTA enters the engine, where a within-page rail steps the four
// views (Structural → Feasible → Opportunity → Matrix).
const PrioritizationEngineSection = lazyRetry(() =>
  import("./saudi-prioritization-section").then((m) => ({ default: m.PrioritizationEngineSection })),
);
// Section 2.2 "Search signals" — the redesign is now the live default. The
// previous implementation in ./search-section.tsx is no longer wired in.
const SearchSection = lazyRetry(() =>
  import("./search-section-redesign").then((m) => ({ default: m.SearchSectionRedesign })),
);
const SentimentSection = lazyRetry(() => import("./sentiment-section").then((m) => ({ default: m.SentimentSection })));
const SourcePrioritizationAdminSection = lazyRetry(() =>
  import("./source-prioritization-admin-section").then((m) => ({ default: m.SourcePrioritizationAdminSection })),
);
const SourceSection = lazyRetry(() => import("./source-section").then((m) => ({ default: m.SourceSection })));
const VisaSection = lazyRetry(() => import("./visa-section").then((m) => ({ default: m.VisaSection })));

type View = "global" | "saudi";

// ── Intelligence · Global scope ────────────────────────────────────────────
// Section IA — identical groups / items / numbering to the previous TopBar,
// just consumed by the left section-tree sidebar instead.
const INTEL_GLOBAL_GROUPS: SectionGroup[] = [
  {
    id: "overview",
    n: "01",
    label: "Global overview",
    items: [
      { id: "global", n: "1.1", label: "Global demand" },
      { id: "inbound", n: "1.2", label: "Inbound tourism" },
      { id: "source", n: "1.3", label: "Source markets" },
      { id: "corridors", n: "1.4", label: "Corridors" },
      { id: "purpose", n: "1.5", label: "Purpose of travel" },
    ],
  },
  // Live demand signals (formerly group 02) lifted out into their own
  // "Demand Signals" workspace (see DEMAND_SIGNALS_GROUPS); the remaining
  // groups renumber up to close the gap.
  {
    id: "drivers",
    n: "02",
    label: "Key drivers",
    items: [
      { id: "connectivity", n: "2.1", label: "Connectivity" },
      { id: "affordability", n: "2.2", label: "Affordability" },
      { id: "visa", n: "2.3", label: "Visa & entry" },
    ],
  },
  {
    id: "forecasts",
    n: "03",
    label: "Forecasts",
    items: [{ id: "forecasts", n: "3.1", label: "Outlook 2026–2035" }],
  },
];

// ── Intelligence · Saudi scope ─────────────────────────────────────────────
// Priority markets used to live here as group 03. As of Plan 15 it's lifted out
// to its own top-level peer (PRIORITIZATION_GROUPS below). Saudi is a *scope* of
// Intelligence, not a peer. The section set mirrors the Global lens (overview /
// live signals / key drivers), ported from the demand-sensing mock into the TQ
// light theme — see `saudi-intel-sections.tsx`.
const INTEL_SAUDI_GROUPS: SectionGroup[] = [
  {
    id: "saudi",
    n: "01",
    label: `${COUNTRY} overview`,
    items: [
      { id: "saudi-demand", n: "1.1", label: `${COUNTRY} demand` },
      { id: "saudi-source", n: "1.2", label: "Source markets" },
      { id: "saudi-destinations", n: "1.3", label: "Destinations" },
      { id: "saudi-target", n: "1.4", label: "Actual vs. target" },
    ],
  },
  // Live signals (formerly group 02) lifted out into the "Demand Signals"
  // workspace (see DEMAND_SIGNALS_GROUPS); Key drivers renumbers up to 02.
  {
    id: "saudi-drivers",
    n: "02",
    label: "Key drivers",
    items: [
      { id: "saudi-connectivity", n: "2.1", label: "Connectivity" },
      { id: "saudi-accommodation", n: "2.2", label: "Accommodation" },
      { id: "saudi-visa", n: "2.3", label: "Visa & entry" },
    ],
  },
];

// ── Demand Signals workspace ───────────────────────────────────────────────
// The live-signals sections, lifted out of Intelligence (Global group 02 +
// Saudi group 02) into their own top-level workspace so both lenses sit
// together. Scoped by geography exactly like Intelligence (design feedback
// 2026-07-26, "saudi and global should be the same format like the dashboard"):
// the two lenses sit behind the sidebar's Global/Saudi segmented control rather
// than as nested "01 Global live demand signals" / "02 Saudi live signals"
// groups. Each scope holds a single `flat` group, so the rail is a plain list
// of sections under the control — no group header restating the pill above it.
// Section bodies (and their in-page header numbers) are unchanged: the Saudi
// items keep their 2.x numbers, matching the numbers their own headers print.
const DEMAND_SIGNALS_GLOBAL_GROUPS: SectionGroup[] = [
  {
    id: "global-signals",
    n: "01",
    label: "Global signals",
    items: [
      // 1.1 carries "What's going viral" while the behavior wall is flagged off.
      { id: "behavior", n: "1.1", label: SHOW_LEGACY_BEHAVIOR_WALL ? "Behavior trends" : "What's going viral" },
      { id: "search", n: "1.2", label: "Search signals" },
      { id: "sentiment", n: "1.3", label: "Sentiment" },
      { id: "booking", n: "1.4", label: "Booking intent" },
    ],
  },
];

const DEMAND_SIGNALS_SAUDI_GROUPS: SectionGroup[] = [
  {
    id: "saudi-signals",
    n: "02",
    label: `${COUNTRY} signals`,
    items: [
      // Mirrors the Global order exactly (1.1 What's going viral → 1.2 Search
      // signals → 1.3 Sentiment → 1.4 Booking intent) — client ask 2026-09-03,
      // labels and the 2.3/2.4 order aligned 2026-09-09.
      { id: "saudi-viral", n: "2.1", label: "What's going viral" },
      { id: "saudi-search", n: "2.2", label: "Search signals" },
      { id: "saudi-sentiment", n: "2.3", label: "Sentiment by source" },
      { id: "saudi-booking", n: "2.4", label: "Booking intent" },
    ],
  },
];

// ── Prioritization peer ────────────────────────────────────────────────────
// The gravity/regression model — a top-level peer of Intelligence, NOT a scope
// of Saudi. Restructured (Plan 16) into Ohoud's linear presentation walk: an
// opening pitch, then the four steps of the engine one screen at a time
// (Structural → Feasible → Opportunity → Matrix), then the model internals.
// Walked front-to-back with the Back/Next pager so it presents like slides.
const PRIORITIZATION_GROUPS: SectionGroup[] = [
  {
    id: "prioritization",
    n: "01",
    label: "The walkthrough",
    // Folded to a single item: the engine's four steps are now within-page
    // views behind the loader, not separate tree sections.
    items: [{ id: "prio-engine", n: "1.1", label: "Prioritization engine" }],
  },
  {
    id: "prioritization-model",
    n: "02",
    label: "Behind the model",
    items: [
      { id: "saudi-potential-process", n: "2.1", label: "Process & lineage" },
      { id: "source-model-admin", n: "2.2", label: "Model admin" },
    ],
  },
];

// ── Segmentation peer ──────────────────────────────────────────────────────
// Track A (genuine-traveler segmentation) — the live Demand-Sensing engine,
// surfaced as two sidebar sections (Explorer + Traveler Archetypes) so it
// navigates like the rest of the dashboard rather than via its own top-bar tabs.
const SEGMENTATION_GROUPS: SectionGroup[] = [
  {
    id: "segmentation",
    n: "01",
    label: "Demand sensing",
    items: [
      { id: "seg-explorer", n: "1.1", label: "Explorer" },
      { id: "seg-segments", n: "1.2", label: "Traveler archetypes" },
      // The Track A console (launch runs, edit workbooks). It used to be an
      // external jump to a standalone /segmentation route; it now renders
      // in-shell like its two siblings, keeping its own access-code gate.
      { id: "seg-console", n: "1.3", label: "Segmentation console" },
    ],
  },
];

// ── Product Matching workspace ──────────────────────────────────────────────
// Product analysis is market-independent (Saudi's bookable supply benchmarked
// against UAE/Egypt/Türkiye/Morocco, plus the gap to the Demand Model targets),
// so it stands on its own rather than sitting inside the per-market GTM plan.
// It is the destination of the landing screen's 05 "Product Matching & Gap
// Analysis" card.
// Restructured 2026-07-28 (S53, Ohoud): the single four-tab section became four
// sidebar sections. 1.1 keeps Framework / Comparable analysis / Gap assessment;
// the old fourth tab (Quality) is now 1.2 in its own right. Both render off the
// same `/gtm/product` payload — the route and the gtm_product_* tables are
// unchanged, this is a presentation split. 1.3 renders off its own
// `/gtm/product-matching` route; 1.4 composes all three.
const PRODUCT_GROUPS: SectionGroup[] = [
  {
    id: "product-supply",
    n: "01",
    label: "Product matching",
    items: [
      { id: "gtm-product", n: "1.1", label: "Product inventory and gap assessment" },
      { id: "gtm-product-quality", n: "1.2", label: "Product quality" },
      { id: "gtm-product-segments", n: "1.3", label: "Product-segment matching" },
      { id: "gtm-product-summary", n: "1.4", label: "Summary & key interventions" },
    ],
  },
];

// ── Go to Market peer (Plans 18–19) ─────────────────────────────────────────
// The full GTM plan, mirroring the prototype's four phases. Phase 01 "Market
// performance" is live (the per-source-market brief built in Plan 18); the
// remaining modules are scaffolded as "coming soon" (Plan 19) so the whole
// plan is navigable in its real shape. Product left this tree for its own
// workspace (see PRODUCT_GROUPS above).
//   01 Market performance · 02 Where to play · 03 How to win · 04 What it takes
const GTM_GROUPS: SectionGroup[] = [
  {
    id: "gtm-perf",
    n: "01",
    label: "Market performance",
    items: [{ id: "gtm-brief", n: "1.1", label: "Market Brief & Performance" }],
  },
  {
    id: "gtm-play",
    n: "02",
    label: "Where to play",
    items: [{ id: "gtm-segments", n: "2.1", label: "Segments" }],
  },
  {
    id: "gtm-win",
    n: "03",
    label: "How to win",
    items: [
      { id: "gtm-distribution", n: "3.1", label: "Distribution" },
      { id: "gtm-promotion", n: "3.2", label: "Promotion" },
    ],
  },
  {
    id: "gtm-takes",
    n: "04",
    label: "What it takes",
    items: [
      { id: "gtm-connectivity", n: "4.1", label: "Connectivity" },
      { id: "gtm-visa", n: "4.2", label: "Visa" },
    ],
  },
];

// Copy for the not-yet-built GTM modules — phase (kicker), title and a
// one-line blurb adapted from the prototype. Rendered as themed "coming soon"
// placeholders until each module is built out.
interface GtmComingSoonCopy {
  n: string;
  phase: string;
  title: string;
  /** Accented last word of the hero title; defaults to "Playbook". */
  titleEm?: string;
  blurb: string;
  tiles: { n: string; t: string; d: string }[];
}

const GTM_COMING_SOON: Record<string, GtmComingSoonCopy> = {
  "gtm-segments": {
    n: "2.1",
    phase: "Where to play",
    title: "Segments",
    blurb: "Traveller personas clustered into behavioural segments - the priority audiences worth winning.",
    tiles: [
      { n: "01", t: "Priority Segments", d: "The behavioural traveller segments worth winning for this market." },
      { n: "02", t: "Segment Profiles", d: "Who each segment is - motivations, spend and trip shape." },
      { n: "03", t: `Fit to ${COUNTRY}`, d: `How well each segment matches ${COUNTRY}'s current product and story.` },
      { n: "04", t: "Where to Focus", d: "The segments to prioritise now, and the ones to leave for later." },
    ],
  },
  "gtm-promotion": {
    n: "3.2",
    phase: "How to win",
    title: "Promotion",
    blurb: "Channels, content and influencers structured into a campaign brief - how to reach the market.",
    tiles: [
      { n: "01", t: "Channels", d: "The media and platforms that reach this market's travellers." },
      { n: "02", t: "Content & Message", d: "The stories and creative that move each priority segment." },
      { n: "03", t: "Influencers & Partners", d: "The voices and partners with real pull in the market." },
      { n: "04", t: "Campaign Brief", d: "Channels, content and partners assembled into one actionable brief." },
    ],
  },
  // Product Matching 1.3 + 1.4 (S53). Ohoud named both sections and supplied
  // the `blurb` wording verbatim, but not their contents — neither has panels
  // or backing data yet, and the scope question is open with her. So they are
  // navigable in their real shape and honestly marked "coming soon" rather than
  // filled with mock analysis. The BLURBS are the client's copy (don't reword);
  // the tiles below are our read of them, not a committed spec.
  "gtm-product-summary": {
    n: "1.4",
    phase: "Product matching",
    title: "Summary & key",
    titleEm: "interventions",
    blurb: "Key product development targets and priorities, ranked by demand weight and supply gap",
    tiles: [
      {
        n: "01",
        t: "Development Targets",
        d: `The product ${COUNTRY} needs to build against the ${VISION_YEAR} targets.`,
      },
      { n: "02", t: "Demand Weight", d: "How much traveller demand sits behind each target." },
      { n: "03", t: "Supply Gap", d: "How far today's observed inventory falls short of it." },
      { n: "04", t: "Ranked Priorities", d: "The interventions ordered by demand weight against supply gap." },
    ],
  },
};

const SCOPE_LABEL: Record<View, string> = {
  global: "Global",
  saudi: COUNTRY_FULL,
};

// One-line "what this says" thesis per section — the slide caption shown in
// present mode so the deck narrates itself. The prioritization walkthrough is
// now one folded section (its four steps are within-page views).
const STEP_WRITEUP: Record<string, string> = {
  "prio-engine": `From the share ${COUNTRY} should hold, to where it should act first - in four steps.`,
};

/** Resolve the section groups for a workspace (and Intelligence's scope). */
function groupsFor(workspace: WorkspaceId, view: View): SectionGroup[] {
  switch (workspace) {
    case "intelligence":
      return view === "saudi" ? INTEL_SAUDI_GROUPS : INTEL_GLOBAL_GROUPS;
    case "demand-signals":
      return view === "saudi" ? DEMAND_SIGNALS_SAUDI_GROUPS : DEMAND_SIGNALS_GLOBAL_GROUPS;
    case "prioritization":
      return PRIORITIZATION_GROUPS;
    case "segmentation":
      return SEGMENTATION_GROUPS;
    case "product":
      return PRODUCT_GROUPS;
    case "gtm":
      return GTM_GROUPS;
  }
}

const SECTION_CHUNKS: Record<string, { preload: () => Promise<unknown> }> = {
  inbound: InboundSection,
  source: SourceSection,
  corridors: CorridorsSection,
  purpose: PurposeSection,
  behavior: BehaviorTrendsSection,
  search: SearchSection,
  booking: BookingSection,
  connectivity: ConnectivitySection,
  affordability: AffordabilitySection,
  visa: VisaSection,
  forecasts: ForecastsSection,
  "saudi-demand": SaudiDemandSection,
  "saudi-source": SaudiSourceMarketSection,
  "saudi-destinations": SaudiDestinationsSection,
  "saudi-target": SaudiTargetSection,
  "saudi-viral": SaudiViralSection,
  "saudi-search": SaudiSearchSection,
  "saudi-booking": SaudiBookingSection,
  "saudi-connectivity": SaudiConnectivitySection,
  "saudi-accommodation": SaudiAccommodationSection,
  "saudi-visa": SaudiVisaSection,
  "prio-engine": PrioritizationEngineSection,
  "saudi-potential-process": SaudiPotentialProcessSection,
  "source-model-admin": SourcePrioritizationAdminSection,
  "seg-explorer": SegmentationExplorerSection,
  "seg-segments": SegmentationSegmentsSection,
  "seg-console": SegmentationConsoleSection,
  "gtm-brief": GtmMarketBriefSection,
  "gtm-connectivity": GtmConnectivitySection,
  "gtm-distribution": GtmDistributionSection,
  "gtm-visa": GtmVisaSection,
  "gtm-product": GtmProductSection,
  "gtm-product-quality": GtmProductSection,
  "gtm-product-segments": GtmProductSection,
  "gtm-product-summary": GtmProductSection,
  "gtm-segments": GtmComingSoon,
  "gtm-promotion": GtmComingSoon,
};

const sectionIds = (workspace: WorkspaceId, view: View): string[] =>
  groupsFor(workspace, view).flatMap((g) => g.items.filter((it) => !it.href).map((it) => it.id));

const WORKSPACE_DOORS: Partial<Record<WorkspaceId, { preload: () => Promise<unknown> }>> = {
  segmentation: EngineLanding,
  product: ProductHub,
};

for (const [id, section] of Object.entries(SECTION_CHUNKS)) sectionPreloader.register(id, section.preload);
for (const w of WORKSPACES) {
  const door = WORKSPACE_DOORS[w.id] ?? SECTION_CHUNKS[sectionIds(w.id, "global")[0] ?? ""];
  if (door) sectionPreloader.register(workspacePreloadKey(w.id), door.preload);
}

const BACKGROUND_PRELOAD_ORDER: string[] = [
  ...WORKSPACES.map((w) => workspacePreloadKey(w.id)),
  ...WORKSPACES.map((w) => workspaceDataKey(w.id)),
  ...new Set(WORKSPACES.flatMap((w) => [...sectionIds(w.id, "global"), ...sectionIds(w.id, "saudi")])),
];
const BACKGROUND_PRELOAD_DELAY_MS = 800;

/** Find which workspace (and Intelligence scope) owns a given section id. Used
    to resolve `initialActive` to the right workspace + scope on first render. */
function locate(id: string): { workspace: WorkspaceId; view: View } {
  const has = (gs: SectionGroup[]) => gs.some((g) => g.items.some((it) => it.id === id));
  if (has(DEMAND_SIGNALS_GLOBAL_GROUPS)) return { workspace: "demand-signals", view: "global" };
  if (has(DEMAND_SIGNALS_SAUDI_GROUPS)) return { workspace: "demand-signals", view: "saudi" };
  if (has(INTEL_GLOBAL_GROUPS)) return { workspace: "intelligence", view: "global" };
  if (has(INTEL_SAUDI_GROUPS)) return { workspace: "intelligence", view: "saudi" };
  if (has(PRIORITIZATION_GROUPS)) return { workspace: "prioritization", view: "global" };
  if (has(SEGMENTATION_GROUPS)) return { workspace: "segmentation", view: "global" };
  if (has(PRODUCT_GROUPS)) return { workspace: "product", view: "global" };
  if (has(GTM_GROUPS)) return { workspace: "gtm", view: "global" };
  return { workspace: "intelligence", view: "global" };
}

/**
 * Resolve a requested section to where it should actually open.
 *
 * Normally that is the workspace `locate` found. But a workspace can be
 * withheld from a deployment (see workspaces.ts), and it still OWNS its section
 * ids — so a restored id from localStorage or a deep link would open a
 * workspace this build does not offer, with a tree that highlights nothing.
 * Falling back moves the active section too, so the switcher, the tree and the
 * rendered section never disagree.
 */
function resolveEntry(id: string): { workspace: WorkspaceId; view: View; active: string } {
  const loc = locate(id);
  if (workspaceAvailable(loc.workspace)) return { ...loc, active: id };
  const fallback = locate("global");
  return { ...fallback, active: groupsFor(fallback.workspace, fallback.view)[0]?.items[0]?.id ?? "global" };
}

const STORAGE_KEY_COLLAPSED = "ow-sidebar-collapsed";

/** Workspaces running the de-chromed shell (design-lead 2026-07-08): no
    breadcrumb/pager row, no sticky source footer. The section-tree carries
    navigation; provenance lives with the data (3.3 lineage, admin ledger).
    Rollout is workspace-by-workspace — add ids here as sections are rebuilt
    to the full-bleed v2 language. */
const CHROMELESS: readonly WorkspaceId[] = [
  "prioritization",
  "intelligence",
  "gtm",
  "segmentation",
  "demand-signals",
  "product",
];

export function Dashboard({
  initialActive,
  sectionOverrides,
}: {
  /** Section id to open on first render (e.g. "visa"). Defaults to "global". */
  initialActive?: string;
  /** Replace a section's body — used by the 3.3 design prototypes. */
  sectionOverrides?: Record<string, ReactNode>;
} = {}) {
  const [theme, setTheme] = useTheme();
  // Below the mobile breakpoint (≤900px) the whole shell is replaced by the
  // phone-native MobileShell (bottom bar + sheets); desktop is untouched. One
  // matchMedia listener, re-rendering only when the viewport crosses 900px.
  const isMobile = useBelow("mobile");
  // Resolve the requested initial section to its owning workspace + scope so the
  // switcher, scope toggle and tree all open in agreement on first render.
  const initial = resolveEntry(initialActive ?? "global");
  const [workspace, setWorkspace] = useState<WorkspaceId>(initial.workspace);
  const [view, setView] = useState<View>(initial.view);
  const [active, setActive] = useState(initial.active);
  // First-run landing hub (Plan 17). A bare load opens on the hub; a deep-link
  // entry (`initialActive` set, e.g. the 3.3 prototype pages) bypasses it.
  const [showHub, setShowHub] = useState(initialActive == null);
  const queryClient = useQueryClient();
  useEffect(() => {
    sectionPreloader.register(workspaceDataKey("prioritization"), () =>
      queryClient.prefetchQuery(sourcePrioritizationQuery(2025)),
    );
    sectionPreloader.register(workspaceDataKey("gtm"), () => queryClient.prefetchQuery(gtmMarketsQuery()));
    if (workspaceAvailable("segmentation")) {
      sectionPreloader.register(workspaceDataKey("segmentation"), () =>
        queryClient.prefetchQuery(explorerQuery(null, COMBINED_RUN_ID)),
      );
    }
    const timer = window.setTimeout(
      () => sectionPreloader.queue(BACKGROUND_PRELOAD_ORDER),
      BACKGROUND_PRELOAD_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [queryClient]);
  useEffect(() => {
    if (!showHub) sectionPreloader.queue(sectionIds(workspace, view), { front: true });
  }, [showHub, workspace, view]);
  // Segmentation's own front door (the dark engine landing + methodology
  // dossier). Shown the first time the workspace is opened in a session and not
  // again — the same contract as the hub above: a briefing on arrival, never a
  // toll gate you re-pay every time you flip back from another workspace. A
  // deep-link entry (`initialActive` set) skips it, exactly as it skips the hub.
  const [segIntroSeen, setSegIntroSeen] = useState(initialActive != null);
  // Product Matching's front door — the four-tile overview of 1.1–1.4. Same
  // contract as the segmentation landing above: shown on every entry into the
  // workspace, dismissed by a tile or by any sidebar section, skipped by a
  // deep-link entry. Without it, entering the workspace lands on 1.1's own
  // landing, which reads as the whole workspace when it is a quarter of it.
  const [productHubSeen, setProductHubSeen] = useState(initialActive != null);
  // Present mode — sidebars + chrome fall away and the section becomes a slide.
  // v1 is offered on the Prioritization walkthrough (each item is already a step).
  const [present, setPresent] = useState(false);
  const [collapsed, setCollapsedRaw] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(STORAGE_KEY_COLLAPSED) === "1";
  });

  const setCollapsed = (next: boolean) => {
    setCollapsedRaw(next);
    try {
      localStorage.setItem(STORAGE_KEY_COLLAPSED, next ? "1" : "0");
    } catch {
      // localStorage may be unavailable in private mode — non-fatal.
    }
  };

  // Mobile (≤900px) off-canvas nav. At these widths the left rail is hidden and
  // a hamburger in the mobile top bar slides it in as a drawer. Desktop never
  // sees this — the CSS below only reveals the top bar/scrim under 900px.
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Esc closes the mobile nav drawer (standard off-canvas dismissal).
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const { data: globalsData } = useGlobals();
  const latestYear = globalsData?.latestYear ?? null;

  // Section 1.1 subtitle is derived from the same hero values the tiles
  // show, plus simple comparisons against the trend history. Numbers,
  // record-flag and growth-comparison are all data-driven.
  // Number-free narrative — the numbers now live in the 1.1 navy hero strip
  // (HeroRow), so the header states the story in words and nothing repeats
  // the flagship figures. Still fully data-driven (record flag +
  // spend-vs-volume comparison).
  const overviewSubtitle = (() => {
    if (!globalsData?.hero || !latestYear) return "";
    const a = globalsData.hero.arrivals.value;
    const otherYears = globalsData.globalTS.filter((p) => p.year !== latestYear).map((p) => p.arrivals);
    const isRecord = otherYears.length > 0 && a > Math.max(...otherYears);
    const spendFaster = globalsData.hero.receipts.yoy > globalsData.hero.arrivals.yoy;
    let s = isRecord
      ? `International tourism reached a new record in ${latestYear}.`
      : `Where international tourism stands in ${latestYear}.`;
    if (spendFaster) s += " Spend is growing faster than volume.";
    return s;
  })();

  const groups = groupsFor(workspace, view);
  const currentGroup = groups.find((g) => g.items.some((it) => it.id === active)) ?? groups[0]!;
  const currentItem = currentGroup.items.find((it) => it.id === active) ?? currentGroup.items[0]!;

  // Safety net: if `active` ever falls outside the visible workspace/scope tree
  // (e.g. a scope flip that didn't reset it), snap to the first valid item.
  useEffect(() => {
    const validIds = groupsFor(workspace, view).flatMap((g) => g.items.filter((it) => !it.href).map((it) => it.id));
    if (!validIds.includes(active)) {
      setActive(validIds[0]!);
    }
  }, [workspace, view, active]);

  const pickItem = (id: string) => {
    setActive(id);
    // On mobile the sidebar is a drawer over the content — picking a section
    // dismisses it so the reader lands on the section, not the menu.
    setDrawerOpen(false);
    // Picking a section from the sidebar leaves the segmentation front door
    // (the engine landing) — the nav is a real escape hatch, not a dead end
    // where "Enter the engine" is the only way forward.
    if (workspace === "segmentation") setSegIntroSeen(true);
    // Same for Product Matching's hub — its tiles route through here too, so a
    // tile click both opens the section and leaves the hub.
    if (workspace === "product") setProductHubSeen(true);
    // Reset scroll so the new section starts at the visible top.
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // Switch workspace (dropdown). Land on the workspace's first section, keeping
  // the current Intelligence scope so flipping away and back doesn't lose it.
  const selectWorkspace = (next: WorkspaceId) => {
    setWorkspace(next);
    setActive(groupsFor(next, view)[0]!.items[0]!.id);
    setPresent(false);
    // Re-arm the segmentation front door on every entry into the workspace (from
    // the dropdown or the hub's "03 Traveler Behaviour" card), so the engine
    // landing plays each time — not just once per session. Clicking a sidebar
    // section (pickItem) or "Enter the engine" dismisses it again.
    if (next === "segmentation") setSegIntroSeen(false);
    // Product Matching re-arms the same way, from the dropdown or the landing
    // screen's "05 Product Matching & Gap Analysis" card.
    if (next === "product") setProductHubSeen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // Enter a workspace from the landing hub — same as a switch, but also leaves
  // the hub. Used by the landing-hub cards that open a whole section.
  const enterWorkspace = (next: WorkspaceId) => {
    selectWorkspace(next);
    setShowHub(false);
  };

  // Deep-link entry from the landing hub: open a specific subsection (resolving
  // its owning workspace + scope), not just a workspace's first item. Used for
  // any card that targets a `{ section }` rather than a whole workspace.
  const enterSection = (sectionId: string) => {
    const loc = resolveEntry(sectionId);
    setWorkspace(loc.workspace);
    setView(loc.view);
    setActive(loc.active);
    setPresent(false);
    setShowHub(false);
    // A card that names a section asked for THAT section — don't interpose the
    // Product workspace's own four-tile front door on the way there.
    setProductHubSeen(true);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // Landing-hub card → either a whole workspace or a deep-linked subsection.
  // A null target is a placeholder card (no destination yet) and does nothing.
  const enterTarget = (t: { workspace: WorkspaceId } | { section: string } | null) => {
    if (!t) return;
    if ("workspace" in t) enterWorkspace(t.workspace);
    else enterSection(t.section);
  };

  // Present-mode keyboard: ←/→ step through the deck, Esc exits.
  // External-link items aren't slides, so they're excluded from the deck
  // (and from the breadcrumb pager, which shares this list).
  const flatItems = groups.flatMap((g) => g.items).filter((it) => !it.href);
  const flatKey = flatItems.map((it) => it.id).join("|");
  // biome-ignore lint/correctness/useExhaustiveDependencies: onKey closes over the current flatItems/pickItem; re-subscribing is keyed on flatKey + active so identity churn doesn't matter.
  useEffect(() => {
    if (!present) return;
    const onKey = (e: KeyboardEvent) => {
      const idx = flatItems.findIndex((it) => it.id === active);
      if (e.key === "Escape") setPresent(false);
      else if (e.key === "ArrowRight" && idx < flatItems.length - 1) pickItem(flatItems[idx + 1]!.id);
      else if (e.key === "ArrowLeft" && idx > 0) pickItem(flatItems[idx - 1]!.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [present, active, flatKey]);

  // Switch scope (Global/Saudi) within the current scoped workspace. Land on
  // the scope's first section. Workspace-relative, not hardcoded to
  // Intelligence — Demand Signals carries the same control.
  const selectView = (next: View) => {
    setView(next);
    setActive(groupsFor(workspace, next)[0]!.items[0]!.id);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // ── Phone-native shell (≤900px) ──────────────────────────────────────────
  // Below the mobile breakpoint the desktop chrome (left tree + content well +
  // present deck) doesn't hold, so we render the re-architected MobileShell —
  // bottom bar + bottom-sheet navigator — around the SAME section content. The
  // desktop paths below are left exactly as they were (only reached when wide).
  if (isMobile) {
    const homeMode = showHub;
    const content = homeMode ? (
      <LandingHub onOpen={enterTarget} />
    ) : workspace === "segmentation" && !segIntroSeen ? (
      <SectionErrorBoundary resetKey="engine-landing">
        <Suspense fallback={null}>
          <EngineLanding onEnter={() => setSegIntroSeen(true)} />
        </Suspense>
      </SectionErrorBoundary>
    ) : (
      <ActiveSection
        id={active}
        number={currentItem.n}
        overviewSubtitle={overviewSubtitle}
        sectionOverrides={sectionOverrides}
        onNavigate={pickItem}
      />
    );
    // The workspace hues are picked to glow on the dark shell, and the same var
    // tints the active tab icon as well as the context dot, so the accent is
    // routed through accentInk — a no-op on dark, a navy-darkened twin on light.
    return (
      <MobileShell
        isHome={homeMode}
        workspace={workspace}
        workspaceLabel={WORKSPACE_LABEL[workspace]}
        accent={accentInk(WORKSPACE_ACCENT[workspace], theme === "light")}
        scoped={WORKSPACE_BY_ID[workspace].scoped}
        view={view}
        groups={groups}
        activeId={active}
        onPickItem={pickItem}
        onPickWorkspace={enterWorkspace}
        onSelectView={selectView}
        onHome={() => setShowHub(true)}
        theme={theme}
        setTheme={setTheme}
        assistantContext={
          homeMode ? { workspace: "landing" } : { workspace, section: `${currentItem.n} ${currentItem.label}` }
        }
      >
        {content}
      </MobileShell>
    );
  }

  // First-run landing — the app opens here until the user enters a workspace
  // (or returns via the switcher's "Back to home"). Deep-link entries skip it.
  if (showHub) {
    return (
      <>
        <LandingHub onOpen={enterTarget} />
        {/* The assistant is reachable before picking a workspace — a question is
            often a faster way in than the funnel. `dark` because the hub's
            near-black backdrop swallows the navy pill. */}
        <AssistantPanel context={{ workspace: "landing" }} variant="dark" />
      </>
    );
  }

  if (present) {
    return (
      <PresentMode
        kicker={`${currentItem.n} · ${currentItem.label}`}
        writeup={STEP_WRITEUP[active] ?? ""}
        items={flatItems}
        activeId={active}
        onPick={pickItem}
        onExit={() => setPresent(false)}
      >
        {renderSectionBody(active, overviewSubtitle, pickItem)}
      </PresentMode>
    );
  }

  return (
    <div
      style={
        {
          "--dash-rail-left": `${collapsed ? TREE_W_COLLAPSED : TREE_W}px`,
          minHeight: "100vh",
          background: C.bg,
          color: C.textHi,
          fontFamily: FU,
        } as CSSProperties
      }
    >
      {/* Mobile top bar (≤900px). The left rail becomes an off-canvas drawer at
          these widths, so a hamburger restores navigation and the wordmark
          restores brand presence — both otherwise live only in the hidden rail.
          Hidden ≥901px by the CSS below. */}
      <div className="dash-topbar">
        <button
          type="button"
          className="dash-hamburger"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
          aria-expanded={drawerOpen}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <line x1="3" y1="6" x2="17" y2="6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <line x1="3" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
            <line x1="3" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </button>
        <div className="dash-topbar-mark">
          <span className="dash-topbar-name">{APP_NAME_LINES[0]}</span>
          <span className="dash-topbar-sub">{APP_NAME_LINES[1]}</span>
        </div>
      </div>

      {/* Scrim behind the open drawer — tap to dismiss. Only rendered while open;
          the CSS keeps it below 900px only (desktop never shows a drawer). */}
      {drawerOpen && (
        <button
          type="button"
          className="dash-scrim"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <SectionTree
        groups={groups}
        workspace={workspace}
        onChangeWorkspace={selectWorkspace}
        onHome={() => setShowHub(true)}
        showScope={WORKSPACE_BY_ID[workspace].scoped}
        activeId={active}
        onPick={pickItem}
        view={view}
        onChangeView={selectView}
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed(!collapsed)}
        drawerOpen={drawerOpen}
        onCloseDrawer={() => setDrawerOpen(false)}
        theme={theme}
        setTheme={setTheme}
      />

      {/* Content well — left margin tracks the rail (fixed) plus the section
          tree's current width so neither overlaps the content. Sticky
          breadcrumb lives inside. */}
      {/* Below 900px the section-tree sidebar becomes an off-canvas drawer (see
          section-tree.tsx), so the content well reclaims the full width and the
          mobile top bar (below) carries the hamburger + wordmark. */}
      <style>{DASH_SHELL_CSS}</style>
      <main
        className={`dash-main${CHROMELESS.includes(workspace) ? "" : " has-footer"}`}
        style={{
          marginLeft: collapsed ? TREE_W_COLLAPSED : TREE_W,
          padding: "0 clamp(20px, 4vw, 56px) 28px",
          maxWidth: 1400,
          transition: "margin-left 220ms ease",
        }}
      >
        {/* Breadcrumb/present row removed for the de-chromed workspaces
            (design-lead 2026-07-08): the section-tree already navigates, and the
            row cost vertical space for no added function. Other workspaces keep
            it until the rollout reaches them. */}
        {!CHROMELESS.includes(workspace) && (
          <BreadcrumbBar
            peer={WORKSPACE_LABEL[workspace]}
            scope={WORKSPACE_BY_ID[workspace].scoped ? SCOPE_LABEL[view] : null}
            group={currentGroup.label}
            item={currentItem.label}
            items={flatItems}
            activeId={active}
            onPick={pickItem}
            onPresent={undefined}
          />
        )}

        {/* Segmentation front door — the landing renders INSIDE the shell (left
            section-tree stays visible) rather than taking over the whole viewport:
            the reader is never stranded with only "Enter the engine", they can
            navigate elsewhere via the sidebar. Its CTA opens the methodology
            dossier; closing that enters, landing on whichever segmentation section
            is active (1.1 Explorer on a fresh switch). Mirrors how Prioritization's
            loader renders in-shell. */}
        {workspace === "segmentation" && !segIntroSeen ? (
          <SectionErrorBoundary resetKey="engine-landing">
            <Suspense fallback={null}>
              <EngineLanding onEnter={() => setSegIntroSeen(true)} />
            </Suspense>
          </SectionErrorBoundary>
        ) : workspace === "product" && !productHubSeen ? (
          /* Product Matching front door — the four sections as tiles, rendered
             in-shell exactly like the segmentation landing above, so the tree
             stays available and the hub is a shortcut, not a gate. Tiles route
             through pickItem, which opens the section and dismisses the hub. */
          <SectionErrorBoundary resetKey="product-hub">
            <Suspense fallback={null}>
              <ProductHub onOpen={pickItem} />
            </Suspense>
          </SectionErrorBoundary>
        ) : (
          <ActiveSection
            id={active}
            number={currentItem.n}
            overviewSubtitle={overviewSubtitle}
            sectionOverrides={sectionOverrides}
            onNavigate={pickItem}
          />
        )}
      </main>
      {/* Sticky source footer removed for the de-chromed workspaces
          (design-lead 2026-07-08): unnecessary chrome, explicitly requested. */}
      {!CHROMELESS.includes(workspace) && (
        <footer
          className="dash-footer"
          style={{
            marginLeft: collapsed ? TREE_W_COLLAPSED : TREE_W,
            borderTop: `1px solid ${C.border}`,
            padding: "13px clamp(20px, 4vw, 56px)",
            background: C.bg,
            transition: "margin-left 220ms ease, left 220ms ease",
          }}
        >
          <div
            style={{
              maxWidth: 1400,
              display: "flex",
              justifyContent: "space-between",
              gap: 18,
              fontFamily: FU,
              fontSize: T.tag,
              color: C.textLo,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              lineHeight: 1.4,
            }}
          >
            {/* This footer renders under EVERY non-chromeless workspace, so it
                was crediting "UN Tourism · WTTC + Oxford Economics" beneath
                Google Trends walls, Aviation Edge tables, Passport Index visa
                maps and the OW Demand Model — three publishers standing in for
                about a dozen, two of which (WTTC, Oxford Economics) feed one
                section, 4.1 Forecasts. A dashboard-wide credit line cannot be
                made true by lengthening it: the list is too long for a footer
                and would still be wrong on the section you happen to be reading.
                Provenance belongs on the panel, where it can name the dataset
                that produced the number in front of you — every Intelligence
                section now carries one. So the footer stops claiming and points
                (S57 source audit, second pass).

                `latestYear` is the /globals year specifically, so it is scoped to
                the headline figures rather than presented as a dashboard-wide
                freshness guarantee. */}
            <div>
              Sources vary by section - see the credit beneath each panel
              {latestYear !== null && ` · headline figures through ${latestYear}`}
            </div>
            <div style={{ whiteSpace: "nowrap" }}>{APP_NAME}</div>
          </div>
        </footer>
      )}

      {/* Assistant drawer (Plan 22). Mounted here so it's absent on the landing
          hub and in present mode, and sits under the dropdowns (z-100) but
          above the footer (z-30). The context tells it which section the user
          is on so vague questions resolve sensibly. Outside the footer's
          CHROMELESS guard — the de-chromed workspaces drop the footer, not the
          assistant. */}
      <AssistantPanel context={{ workspace, section: `${currentItem.n} ${currentItem.label}` }} />
    </div>
  );
}

/**
 * Render whichever sub-section is currently active. Each item renders ONLY
 * its own section — page-per-section navigation. Section content is unchanged
 * from before; we just wrap it in <SectionShell> so the right-rail "On this
 * page" outline can scroll-spy the section's sub-blocks (the
 * `[data-row-anchor]` markers each section emits).
 */
/** Sections that render their own top-of-content step navigation and therefore
 *  suppress the SectionShell right-rail outline. */
const TOP_NAV_SECTIONS = new Set([
  "gtm-connectivity",
  "gtm-distribution",
  "gtm-visa",
  "seg-explorer",
  "seg-segments",
  "seg-console",
  // Intelligence · Global overview (1.1–1.5) — the IntelSectionNav command bar
  // replaces the shell breadcrumb + the right-rail outline across all five.
  "global",
  "inbound",
  "source",
  "corridors",
  "purpose",
  // Prioritization · The walkthrough — one folded engine section on IntelShell +
  // the within-page IntelSectionNav command bar, so it carries its own top nav
  // and drops the right rail.
  "prio-engine",
  // Prioritization · Behind the model (2.x) — internal screens converted to the
  // observatory shell; they carry their own opening, so drop the right rail.
  "saudi-potential-process",
  "source-model-admin",
  // Intelligence · Key drivers — 2.1 Connectivity now carries its own
  // IntelSectionNav + StepRail, so it drops the shell right-rail too.
  "connectivity",
  // 2.2 Affordability + 2.3 Visa & entry — restyled to the IntelSectionNav
  // command bar (single-panel, empty rail), so they drop the right-rail too.
  "affordability",
  "visa",
  // 3.1 Forecasts — now carries its own IntelSectionNav + 3-step rail
  // (Outlook / Trajectory / Cross-institution), so it drops the right-rail.
  "forecasts",
  // Intelligence · Saudi · overview (1.1–1.4) — 1.1 Demand (stat tiles + chart),
  // 1.2 Source markets (flair map), 1.3 Destinations (flair province map) and
  // 1.4 Actual vs. target (2-step rail) all carry their own IntelSectionNav
  // command bar, so they drop the shell right-rail like their Global twins.
  "saudi-demand",
  "saudi-source",
  "saudi-destinations",
  "saudi-target",
  // Intelligence · Saudi · Key drivers (02) — 2.1 Connectivity (3-panel rail),
  // 2.2 Accommodation (Pipeline / Actual rail) and 2.3 Visa & entry (single
  // panel) all carry their own IntelSectionNav command bar, so they drop the
  // shell right-rail like their Global twins.
  "saudi-connectivity",
  "saudi-accommodation",
  "saudi-visa",
  // Demand Signals workspace — Global live signals (1.1–1.4) and Saudi live
  // signals (2.1–2.3) all rebuilt onto IntelShell + their own IntelSectionNav
  // command bar (orange workspace hue), so they drop the shell right-rail.
  "behavior",
  "search",
  "sentiment",
  "booking",
  "saudi-viral",
  "saudi-search",
  "saudi-booking",
  "saudi-sentiment",
  // Product Matching workspace — 1.1 Product inventory and gap assessment
  // (Framework / Comparable / Gap) and 1.2 Product quality, both on the
  // full-bleed observatory shell with their own slim command bar, so they drop
  // the shell right-rail.
  "gtm-product",
  "gtm-product-quality",
  "gtm-product-segments",
  "gtm-product-summary",
]);

function ActiveSection({
  id,
  number,
  overviewSubtitle,
  sectionOverrides,
  onNavigate,
}: {
  id: string;
  number: string;
  overviewSubtitle: string;
  sectionOverrides?: Record<string, ReactNode>;
  onNavigate?: (id: string) => void;
}) {
  const inner = sectionOverrides?.[id] ?? renderSectionBody(id, overviewSubtitle, onNavigate);
  if (!inner) return null;
  // Sections that carry their own top-level step navigation suppress the shell's
  // right-rail outline entirely (GTM 4.1 Connectivity + the Segmentation
  // sections, which now switch Explorer ↔ Archetypes from a top StepRail).
  const railMode = TOP_NAV_SECTIONS.has(id) ? "none" : "collapsible";
  return (
    <SectionShell railMode={railMode} sectionNumber={number} header={null}>
      {/* Lazy section chunks resolve here; the fallback keeps the content
          column from collapsing during the brief load on first visit. */}
      <SectionErrorBoundary resetKey={id}>
        <Suspense fallback={<SectionSkeleton />}>{inner}</Suspense>
      </SectionErrorBoundary>
    </SectionShell>
  );
}

function renderSectionBody(id: string, overviewSubtitle: string, onNavigate?: (id: string) => void) {
  switch (id) {
    case "global":
      return <GlobalSection subtitle={overviewSubtitle} />;
    case "inbound":
      return <InboundSection />;
    case "source":
      return <SourceSection />;
    case "corridors":
      return <CorridorsSection />;
    case "purpose":
      return <PurposeSection />;
    case "behavior":
      return <BehaviorTrendsSection />;
    case "search":
      return <SearchSection />;
    case "sentiment":
      // Section 1.3 gated to "coming soon" pending data sign-off, but rendered in
      // the FULL new UI (shell + command bar), not the old SectionHeader. The
      // live implementation lives in `sentiment-section.tsx` (<SentimentSection />)
      // — swap this for `<SentimentSection />` to un-gate once the data is cleared.
      //
      // The lede named Instagram, X, Reddit and TripAdvisor — see the identical
      // note on the Saudi 2.3 twin below. The pipeline behind the live section is
      // Reddit + Google News only, and Reddit 403s, so the score is news-only.
      // The first S57 pass left it standing on the grounds that the panel carries
      // no data and the copy is the client's (S29); on the second pass that no
      // longer holds. "Coming soon" describes what the section WILL be, so a
      // platform list here is a forward promise we have no path to keeping — the
      // IG/X/TripAdvisor scrapers under `services/scrapers/*` feed Track A
      // segmentation, not this score. It now describes the actual feed, which
      // also means un-gating no longer depends on anyone remembering this note.
      return (
        <ComingSoonSection
          id="sentiment"
          n="1.3"
          kicker="Sentiment signals · What they're saying"
          head={
            <>
              The global <AccentWord ws="demand-signals">conversation</AccentWord> about destinations
            </>
          }
          lede="Overall sentiment score per destination (positive and negative coverage scored across Google News country editions and Reddit travel communities)"
        />
      );
    case "booking":
      return <BookingSection />;
    case "connectivity":
      return <ConnectivitySection />;
    case "affordability":
      return <AffordabilitySection />;
    case "visa":
      return <VisaSection />;
    case "forecasts":
      return <ForecastsSection />;
    case "saudi-demand":
      return <SaudiDemandSection />;
    case "saudi-source":
      return <SaudiSourceMarketSection />;
    case "saudi-destinations":
      return <SaudiDestinationsSection />;
    case "saudi-target":
      return <SaudiTargetSection />;
    case "saudi-viral":
      return <SaudiViralSection />;
    case "saudi-search":
      return <SaudiSearchSection />;
    case "saudi-booking":
      return <SaudiBookingSection />;
    case "saudi-connectivity":
      return <SaudiConnectivitySection />;
    case "saudi-accommodation":
      return <SaudiAccommodationSection />;
    case "saudi-visa":
      return <SaudiVisaSection />;
    case "prio-engine":
      return <PrioritizationEngineSection />;
    case "saudi-potential-process":
      return <SaudiPotentialProcessSection />;
    case "source-model-admin":
      return <SourcePrioritizationAdminSection />;
    case "saudi-sentiment":
      // Saudi 2.3 gated to "coming soon" pending data sign-off, but rendered in
      // the FULL new UI (shell + command bar), not the old SectionHeader. The
      // live implementation lives in `saudi-intel-sections.tsx`
      // (<SaudiSentimentSection />) — swap this render to un-gate once cleared.
      //
      // The lede used to be the client's S29 wording, naming Instagram, X, Reddit
      // and TripAdvisor. The importer's source union is
      // `SentimentSource = "reddit" | "news"` — Reddit travel subs plus Google
      // News — and Reddit is 403-blocked today, so the figure is news-only. The
      // IG/X/TripAdvisor scrapers under `services/scrapers/*` feed Track A
      // segmentation, not this score, so three of the four named platforms were
      // never in this path at all. Rewritten to the real feed on the second S57
      // pass; the live twin at 1.3 Sentiment carries the same string, and the
      // rendered section's own chip is DERIVED from the sources a snapshot
      // actually holds (`feedLabel` in sentiment-section.tsx), so if Reddit
      // access returns the chip picks it up without an edit here.
      return (
        <ComingSoonSection
          id="saudi-sentiment"
          n="2.3"
          kicker={`${COUNTRY} · Live signals`}
          head={
            <>
              Sentiment by <AccentWord ws="demand-signals">source</AccentWord>
            </>
          }
          lede={`${COUNTRY} sentiment score (positive and negative coverage scored across Google News country editions and Reddit travel communities)`}
        />
      );
    case "seg-explorer":
      return <SegmentationExplorerSection onNavigate={onNavigate} />;
    case "seg-segments":
      return <SegmentationSegmentsSection onNavigate={onNavigate} />;
    case "seg-console":
      return <SegmentationConsoleSection onNavigate={onNavigate} />;
    case "gtm-brief":
      return <GtmMarketBriefSection />;
    case "gtm-connectivity":
      return <GtmConnectivitySection />;
    case "gtm-distribution":
      return <GtmDistributionSection />;
    case "gtm-visa":
      return <GtmVisaSection />;
    case "gtm-product":
      return <GtmProductSection variant="inventory" />;
    case "gtm-product-quality":
      return <GtmProductSection variant="quality" />;
    case "gtm-product-segments":
      return <GtmProductSection variant="segments" />;
    case "gtm-product-summary":
      return <GtmProductSection variant="summary" />;
    case "gtm-segments":
    case "gtm-promotion": {
      const m = GTM_COMING_SOON[id]!;
      return (
        <GtmComingSoon n={m.n} phase={m.phase} title={m.title} titleEm={m.titleEm} blurb={m.blurb} tiles={m.tiles} />
      );
    }
    default:
      return null;
  }
}

/**
 * Minimal top pager — the slide-walk control, folded into the breadcrumb row.
 * Quiet ‹ › arrows + an "n / total" counter; the full prev/next labels live in
 * the tooltips so the chrome stays out of the way. Renders nothing when the
 * peer has a single section. This is what lets the Prioritization walkthrough
 * (and the rest of the dashboard) step forward like a deck.
 */
function TopPager({
  items,
  activeId,
  onPick,
}: {
  items: SectionItem[];
  activeId: string;
  onPick: (id: string) => void;
}) {
  const idx = items.findIndex((it) => it.id === activeId);
  if (idx < 0 || items.length <= 1) return null;
  const prev = idx > 0 ? items[idx - 1] : null;
  const next = idx < items.length - 1 ? items[idx + 1] : null;
  return (
    <div className="tpager">
      <button
        type="button"
        className="tpager-arw"
        disabled={!prev}
        onClick={() => prev && onPick(prev.id)}
        title={prev ? `${prev.n} · ${prev.label}` : undefined}
        aria-label="Previous section"
      >
        ‹
      </button>
      <span className="tpager-count">
        {idx + 1} / {items.length}
      </span>
      <button
        type="button"
        className="tpager-arw"
        disabled={!next}
        onClick={() => next && onPick(next.id)}
        title={next ? `${next.n} · ${next.label}` : undefined}
        aria-label="Next section"
      >
        ›
      </button>
    </div>
  );
}

/**
 * Present mode (Plan 16, Workstream D). The rail, tree and breadcrumb fall away;
 * the active section takes the full viewport as a slide — a quiet top strip with
 * the step label, a one-line thesis (the "writeup"), then the section's own
 * visual on a centered stage. Step with ←/→, leave with Esc or the ✕.
 */
function PresentMode({
  kicker,
  writeup,
  items,
  activeId,
  onPick,
  onExit,
  children,
}: {
  kicker: string;
  writeup: string;
  items: SectionItem[];
  activeId: string;
  onPick: (id: string) => void;
  onExit: () => void;
  children: ReactNode;
}) {
  const idx = items.findIndex((it) => it.id === activeId);
  const prev = idx > 0 ? items[idx - 1] : null;
  const next = idx >= 0 && idx < items.length - 1 ? items[idx + 1] : null;
  return (
    <div className="present">
      <div className="present-top">
        <span className="present-kicker">{kicker}</span>
        <div className="present-ctrls">
          <span className="present-count">
            {idx + 1} / {items.length}
          </span>
          <button
            type="button"
            className="present-arw"
            disabled={!prev}
            onClick={() => prev && onPick(prev.id)}
            aria-label="Previous"
          >
            ‹
          </button>
          <button
            type="button"
            className="present-arw"
            disabled={!next}
            onClick={() => next && onPick(next.id)}
            aria-label="Next"
          >
            ›
          </button>
          <button
            type="button"
            className="present-exit"
            onClick={onExit}
            title="Exit (Esc)"
            aria-label="Exit present mode"
          >
            ✕
          </button>
        </div>
      </div>

      {writeup && <p className="present-writeup">{writeup}</p>}

      <div className="present-stage">
        <PresentContext.Provider value={true}>
          {/* Sections are code-split (lazy); the stage needs its own Suspense
              boundary since it renders outside the main shell's boundary. */}
          <SectionErrorBoundary>
            <Suspense fallback={<SectionSkeleton minHeight="50vh" />}>{children}</Suspense>
          </SectionErrorBoundary>
        </PresentContext.Provider>
      </div>

      <style>{`
        .present {
          position: fixed;
          inset: 0;
          z-index: 60;
          background: ${C.bg};
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          padding: 26px clamp(28px, 7vw, 120px) 64px;
        }
        .present-top {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
        }
        .present-kicker {
          font-family: ${FU};
          font-size: ${T.micro}px;
          font-weight: ${W.medium};
          letter-spacing: 0;
          text-transform: uppercase;
          color: ${C.textMd};
        }
        .present-ctrls { display: flex; align-items: center; gap: 8px; }
        .present-count {
          font-family: ${FU};
          font-size: ${T.micro}px;
          color: ${C.textLo};
          letter-spacing: 0;
          font-variant-numeric: tabular-nums;
          margin-right: 2px;
        }
        .present-arw, .present-exit {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid ${C.border};
          background: ${C.surface};
          color: ${C.textMd};
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
          transition: border-color 140ms ease, color 140ms ease;
        }
        .present-arw:hover:not(:disabled), .present-exit:hover { border-color: ${C.borderStrong}; color: ${C.textHi}; }
        .present-arw:disabled { opacity: 0.4; cursor: default; }
        .present-writeup {
          font-family: ${FS};
          font-size: clamp(20px, 2.4vw, 28px);
          font-weight: ${W.light};
          line-height: 1.3;
          letter-spacing: 0;
          color: ${C.textHi};
          max-width: 900px;
          margin: 26px 0 10px;
        }
        .present-stage {
          width: 100%;
          max-width: 1180px;
          margin: 18px auto 0;
        }
        /* Trim the section's own bottom margin inside the stage. */
        .present-stage > section { margin-bottom: 0 !important; }
      `}</style>
    </div>
  );
}

/**
 * In-main sticky breadcrumb. Lives inside `<main>` (so it spans the content
 * column, not the full viewport), pins to the top as the user scrolls, and
 * has a soft gradient fade below so content dissolves into the page
 * background as it scrolls behind.
 */
function BreadcrumbBar({
  peer,
  scope,
  group,
  item,
  items,
  activeId,
  onPick,
  onPresent,
}: {
  peer: string;
  /** Intelligence scope (Global/Saudi). Null for non-scoped peers. */
  scope: string | null;
  group: string;
  item: string;
  items: SectionItem[];
  activeId: string;
  onPick: (id: string) => void;
  /** When set, renders a "Present" button that enters slide mode. */
  onPresent?: () => void;
}) {
  // Lead with the peer, then scope (Intelligence only), group, and the
  // current item. Filtering keeps the separators correct when scope is absent.
  const segs = [peer, scope, group]
    .filter((s): s is string => Boolean(s))
    // Drop a group that just repeats the peer/scope above it (placeholder peers
    // name their single group after the peer) so we don't render "X / X".
    // Case-insensitive so "Go to Market / Go to market" collapses too.
    .filter((s, i, arr) => s.toLowerCase() !== (arr[i - 1] ?? "").toLowerCase());
  return (
    <div className="bcrumb">
      <div className="bcrumb-inner">
        <div className="bcrumb-trail">
          {segs.map((s, i) => (
            <Fragment key={`${i}-${s}`}>
              <span className="bcrumb-seg">{s}</span>
              <span className="bcrumb-sep" aria-hidden="true">
                /
              </span>
            </Fragment>
          ))}
          <span className="bcrumb-seg bcrumb-current">{item}</span>
        </div>
        <div className="bcrumb-tools">
          {onPresent && (
            <button type="button" className="bcrumb-present" onClick={onPresent} title="Present (full screen)">
              Present
            </button>
          )}
          <TopPager items={items} activeId={activeId} onPick={onPick} />
        </div>
      </div>
      <style>{BREADCRUMB_STYLES}</style>
    </div>
  );
}

// Hoisted so the CSS string isn't rebuilt every time the breadcrumb re-renders
// (which happens on every scope/section change and globals load). Static tokens.
const BREADCRUMB_STYLES = `
        .bcrumb {
          position: sticky;
          top: 0;
          z-index: 30;
          background: ${C.bg};
          padding: 20px 0 10px;
          margin-bottom: 18px;
        }
        .bcrumb-inner {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .bcrumb-trail {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          min-width: 0;
          font-family: ${FU};
          font-size: ${T.cap}px;
          letter-spacing: 0;
          color: ${C.textMd};
        }
        .bcrumb-seg { white-space: nowrap; }
        .bcrumb-current {
          color: ${C.textHi};
          font-weight: ${W.medium};
        }
        .bcrumb-sep { color: ${C.textLo}; }
        .bcrumb-tools {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-left: auto;
          flex-shrink: 0;
        }
        .bcrumb-present {
          font-family: ${FU};
          font-size: ${T.micro}px;
          font-weight: ${W.medium};
          letter-spacing: 0;
          color: ${C.textMd};
          background: ${C.surface};
          border: 1px solid ${C.border};
          border-radius: 6px;
          padding: 5px 11px;
          cursor: pointer;
          transition: border-color 140ms ease, color 140ms ease;
        }
        .bcrumb-present:hover { border-color: ${C.borderStrong}; color: ${C.textHi}; }
        .tpager {
          display: flex;
          align-items: center;
          gap: 9px;
          flex-shrink: 0;
        }
        .tpager-count {
          font-family: ${FU};
          font-size: ${T.micro}px;
          color: ${C.textLo};
          letter-spacing: 0;
          font-variant-numeric: tabular-nums;
        }
        .tpager-arw {
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid ${C.border};
          background: ${C.surface};
          color: ${C.textMd};
          border-radius: 8px;
          cursor: pointer;
          font-size: 18px;
          line-height: 1;
          transition: border-color 140ms ease, color 140ms ease;
        }
        .tpager-arw:hover:not(:disabled) { border-color: ${C.borderStrong}; color: ${C.textHi}; }
        .tpager-arw:disabled { opacity: 0.4; cursor: default; }
        .bcrumb::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          top: 100%;
          height: 24px;
          background: linear-gradient(to bottom, ${C.bg}, rgba(0,0,0,0));
          pointer-events: none;
        }
      `;
